import React, { useState, useEffect } from 'react'
import { X, CheckCircle, AlertCircle, Info } from 'lucide-react'

export type ToastType = 'success' | 'error' | 'info'

interface ToastProps {
  message: string
  type: ToastType
  duration?: number
  onClose: () => void
  index: number
}

const Toast: React.FC<ToastProps> = ({ message, type, duration = 3000, onClose, index }) => {
  const [isVisible, setIsVisible] = useState(true)
  const [progress, setProgress] = useState(100)
  
  useEffect(() => {
    // Start progress bar animation
    const startTime = Date.now()
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100)
      setProgress(remaining)
      
      if (remaining <= 0) {
        clearInterval(interval)
        setIsVisible(false)
        setTimeout(onClose, 300) // Allow time for exit animation
      }
    }, 10)
    
    return () => clearInterval(interval)
  }, [duration, onClose])
  
  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle size={20} className="text-white" />
      case 'error':
        return <AlertCircle size={20} className="text-white" />
      case 'info':
        return <Info size={20} className="text-white" />
    }
  }
  
  const getBackgroundColor = () => {
    switch (type) {
      case 'success':
        return 'bg-[#4C704C]'
      case 'error':
        return 'bg-red-600 dark:bg-red-700'
      case 'info':
        return 'bg-[#9d8e72]'
    }
  }
  
  const getProgressColor = () => {
    switch (type) {
      case 'success':
        return 'bg-white/30'
      case 'error':
        return 'bg-white/30'
      case 'info':
        return 'bg-white/30'
    }
  }
  
  return (
    <div 
      className={`max-w-md transform transition-all duration-300 ${
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'
      }`}
      style={{ zIndex: 1000 - index }} // Higher index = lower z-index to stack properly
    >
      <div className={`flex flex-col rounded-lg shadow-md ${getBackgroundColor()} text-white`}>
        <div className="flex items-center p-3">
          <div className="flex-shrink-0 mr-3">
            {getIcon()}
          </div>
          <div className="flex-1 mr-2">
            <p className="text-sm font-medium text-white">{message}</p>
          </div>
          <button 
            onClick={() => {
              setIsVisible(false)
              setTimeout(onClose, 300)
            }}
            className="flex-shrink-0 p-1 rounded-full hover:bg-white/10"
          >
            <X size={16} className="text-white" />
          </button>
        </div>
        
        {/* Progress bar */}
        <div className="h-1 w-full bg-black/10 overflow-hidden">
          <div 
            className={`h-full ${getProgressColor()} transition-all duration-100 ease-linear`}
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      </div>
    </div>
  )
}

export default Toast